/**
 * Last repairs around `Lift` (W1-07b formats-package; used by `importPackage`): data that `normalizeRpe`, the legacy
 * importers or the extra.json decoder pass through but the document model cannot hold (DESIGN.md §7.2: repaired and
 * reported, never silently changed). The export self-check runs `repairIrForModel` and `reportNoteOrder` on what it
 * reads back too and fails on any entry, so it never passes output these repairs would change (CR-W1-07b-8, W3-02b:
 * the repairs stay here instead of moving into `normalizeRpe`, which the legacy importers do not run;
 * CR-W1-07b-11: `repairDocForModel` stays the safety net for collapsed extra.json keyframes).
 *
 * Before `Lift` (`repairIrForModel`):
 *  - Control points (`posControl`, `alphaControl`, `sizeControl`, `yControl`) out of distance order: Phira evaluates
 *    a control as a keyframe track over the note distance and assumes increasing keys (F§6.2, F§12), so with
 *    unsorted points what it shows depends on the notes drawn before. They are sorted by `x` (stable: points at the
 *    same distance keep their order, every point keeps the easing of the interval ending at it), 'changed'.
 *  - Negative `linkgroup` values: RPE link groups are 0 (none) or positive and the model stores non-negative ids;
 *    Phira ignores link groups. They become 0, 'normalized'.
 *  - BPM points so close to 0 that `60 / bpm` overflows (a positive subnormal such as 5e-324): Phira's tempo walk
 *    computes infinite times from them (F§3.2), the model cannot place anything after them, and extra.json
 *    rejects such a list. A later point takes the BPM before it (as if it were dropped); a first point takes the
 *    next usable BPM (120 when there is none), 'changed'.
 *  - A negative effective RPEVersion (`"-5"`, or an integer such as 4294967295 that Phira wraps into the i32 range):
 *    the model keeps the version >= 0 (RPE never writes a negative one). The version only picks the RPE 1.7 speed
 *    sub-mode (>= 170 Modern, below Legacy-170; F§2.2, F§7.3), so it becomes 160, Phira's default, which plays
 *    exactly the same (Legacy-170), 'normalized'.
 *
 * Notes an RPE chart lists out of time order (`reportNoteOrder`; not for the legacy formats, whose two note lists per
 * line have no order of their own): the document orders each line's notes by time, then position, then source order
 * (`compareNotes`), so an export writes them in that order. Phira sorts the notes of every line itself (F§11), so
 * nothing it plays changes: one 'normalized' entry folding the lines whose order changes, at the first of them.
 *
 * After `Lift` (`repairDocForModel`): a track whose first key jumps (vIn differs from vOut), which the model forbids.
 * It comes from keyframes that collapse onto one beat, e.g. extra.json times converted from the file's own BPM list
 * into the chart's beats (already reported 'changed' by the extra.json decoder). Phira holds the value the jump
 * leaves from there on; the key's arriving value becomes that value too (no jump), 'changed'.
 */

import type { IrBpm, IrChart, IrEvent, IrExtended, IrLayer, IrLine, IrMeta } from '../../core/ir/types';
import { trackKeys, trackValueEquals } from '../../core/model/invariants';
import type { ChartDoc, DocIndexes, Key, KeyId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { NonEmpty } from '../../core/util/types';
import type { ReportSink } from './report';

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

const CONTROL_KEYS = ['posControl', 'alphaControl', 'sizeControl', 'yControl'] as const;
const LAYER_LISTS = ['alphaEvents', 'moveXEvents', 'moveYEvents', 'rotateEvents', 'speedEvents'] as const;
/** Extended lists of numbers (colour, text and GIF lists are handled one by one). */
const NUMBER_EXTENDED_LISTS = ['scaleXEvents', 'scaleYEvents', 'inclineEvents', 'paintEvents'] as const;

function isSorted(points: ReadonlyArray<{ readonly x: number }>): boolean {
  for (let k = 1; k < points.length; k++) {
    const a = points[k - 1];
    const b = points[k];
    if (a !== undefined && b !== undefined && !(b.x >= a.x)) return false;
  }
  return true;
}

/** Control curves of one line sorted by distance (null when nothing changed). */
function sortControls(line: IrLine, li: number, r: ReportSink): Mutable<IrLine> | null {
  let next: Mutable<IrLine> | null = null;
  for (const key of CONTROL_KEYS) {
    const points = line[key];
    if (points === undefined || isSorted(points)) continue;
    next ??= { ...line };
    next[key] = points
      .map((p, at) => ({ p, at }))
      .sort((a, b) => a.p.x - b.p.x || a.at - b.at)
      .map(({ p }) => p);
    r.add('changed', 'pkg.report.controlOrderSorted', { field: key }, `judgeLineList[${String(li)}].${key}`, li);
  }
  return next;
}

/** An event list with negative link groups set to 0 (the same list when there is none). */
function fixLinks<V>(list: NonEmpty<IrEvent<V>>, path: string, li: number, r: ReportSink): NonEmpty<IrEvent<V>> {
  let count = 0;
  let first = -1;
  const fix = (e: IrEvent<V>, i: number): IrEvent<V> => {
    if (e.linkgroup >= 0) return e;
    count++;
    if (first < 0) first = i;
    return { ...e, linkgroup: 0 };
  };
  const [head, ...rest] = list;
  const fixed: NonEmpty<IrEvent<V>> = [fix(head, 0), ...rest.map((e, i) => fix(e, i + 1))];
  if (count === 0) return list;
  r.add('normalized', 'pkg.report.linkgroupNegative', undefined, `${path}[${String(first)}].linkgroup`, li, count);
  return fixed;
}

function fixLayerLinks(layer: IrLayer, path: string, li: number, r: ReportSink): IrLayer {
  let next: Mutable<IrLayer> | null = null;
  for (const key of LAYER_LISTS) {
    const list = layer[key];
    if (list === undefined) continue;
    const fixed = fixLinks(list, `${path}.${key}`, li, r);
    if (fixed === list) continue;
    next ??= { ...layer };
    next[key] = fixed;
  }
  return next ?? layer;
}

function fixExtendedLinks(ext: IrExtended, path: string, li: number, r: ReportSink): IrExtended {
  const next: Mutable<IrExtended> = { ...ext };
  let changed = false;
  const at = (key: string): string => `${path}.${key}`;
  for (const key of NUMBER_EXTENDED_LISTS) {
    const list = ext[key];
    if (list === undefined) continue;
    const fixed = fixLinks(list, at(key), li, r);
    if (fixed === list) continue;
    next[key] = fixed;
    changed = true;
  }
  const color = ext.colorEvents;
  if (color !== undefined) {
    const fixed = fixLinks(color, at('colorEvents'), li, r);
    if (fixed !== color) {
      next.colorEvents = fixed;
      changed = true;
    }
  }
  const text = ext.textEvents;
  if (text !== undefined) {
    const fixed = fixLinks(text, at('textEvents'), li, r);
    if (fixed !== text) {
      next.textEvents = fixed;
      changed = true;
    }
  }
  const gif = ext.gifEvents;
  // `[]` (auto-play) has no event to repair.
  if (gif !== undefined && gif.length > 0) {
    const list = gif as NonEmpty<IrEvent<number>>;
    const fixed = fixLinks(list, at('gifEvents'), li, r);
    if (fixed !== list) {
      next.gifEvents = fixed;
      changed = true;
    }
  }
  return changed ? next : ext;
}

/** One line repaired (null when nothing changed). */
function repairLine(line: IrLine, li: number, r: ReportSink): IrLine | null {
  let next = sortControls(line, li, r);
  const base = `judgeLineList[${String(li)}]`;
  let layers: Array<IrLayer | null> | null = null;
  for (let i = 0; i < line.eventLayers.length; i++) {
    const layer = line.eventLayers[i];
    if (layer === undefined || layer === null) continue;
    const fixed = fixLayerLinks(layer, `${base}.eventLayers[${String(i)}]`, li, r);
    if (fixed === layer) continue;
    layers ??= [...line.eventLayers];
    layers[i] = fixed;
  }
  if (layers !== null) {
    next ??= { ...line };
    next.eventLayers = layers;
  }
  const ext = line.extended;
  if (ext !== null && ext !== 'absent') {
    const fixed = fixExtendedLinks(ext, `${base}.extended`, li, r);
    if (fixed !== ext) {
      next ??= { ...line };
      next.extended = fixed;
    }
  }
  return next;
}

/** Whether Phira's tempo walk gets finite times from a BPM (F§3.2: seconds per beat = 60 / bpm). */
function bpmUsable(bpm: number): boolean {
  return bpm > 0 && Number.isFinite(60 / bpm);
}

const FALLBACK_BPM = 120;

/** The BPM list with unusable values replaced (see the module header; the same list when all are usable). */
function repairBpm(list: IrChart['BPMList'], r: ReportSink): IrChart['BPMList'] {
  if (list.every((p) => bpmUsable(p.bpm))) return list;
  const firstUsable = list.find((p) => bpmUsable(p.bpm))?.bpm ?? FALLBACK_BPM;
  let prev: number | null = null;
  const fix = (p: IrBpm, i: number): IrBpm => {
    if (bpmUsable(p.bpm)) {
      prev = p.bpm;
      return p;
    }
    const used = prev ?? firstUsable;
    r.add('changed', 'pkg.report.bpmUnusable', { bpm: String(p.bpm), used: String(used) }, `BPMList[${String(i)}].bpm`);
    return { ...p, bpm: used };
  };
  const [head, ...rest] = list;
  return [fix(head, 0), ...rest.map((p, i) => fix(p, i + 1))];
}

/** Phira's RPEVersion default (F§2.2); every version below 170 plays the Legacy-170 speed sub-mode. */
const DEFAULT_RPE_VERSION = 160;

/** META with a negative effective RPEVersion replaced (see the module header; the same META otherwise). */
function repairRpeVersion(meta: IrMeta, r: ReportSink): IrMeta {
  if (meta.rpeVersionEffective >= 0) return meta;
  r.add(
    'normalized',
    'pkg.report.rpeVersionNegative',
    { value: String(meta.rpeVersionEffective), used: DEFAULT_RPE_VERSION },
    'META.RPEVersion',
  );
  return { ...meta, RPEVersion: DEFAULT_RPE_VERSION, rpeVersionEffective: DEFAULT_RPE_VERSION };
}

/** The chart with every repair of the module header applied, each reported to `r`. */
export function repairIrForModel(input: IrChart, r: ReportSink): IrChart {
  const bpm = repairBpm(input.BPMList, r);
  const meta = repairRpeVersion(input.META, r);
  const chart = bpm === input.BPMList && meta === input.META ? input : { ...input, BPMList: bpm, META: meta };
  let lines: IrLine[] | null = null;
  for (let li = 0; li < chart.judgeLineList.length; li++) {
    const line = chart.judgeLineList[li];
    if (line === undefined) continue;
    const next = repairLine(line, li, r);
    if (next === null) continue;
    lines ??= [...chart.judgeLineList];
    lines[li] = next;
  }
  return lines === null ? chart : { ...chart, judgeLineList: lines };
}

/** The document with first-key jumps removed (see the module header), reported to `r`. */
export function repairDocForModel(doc: ChartDoc, idx: DocIndexes, r: ReportSink): ChartDoc {
  let keys: Map<KeyId, Key> | null = null;
  let count = 0;
  for (const trackId of doc.tracks.keys()) {
    const first = trackKeys(doc, idx, trackId)[0];
    if (first === undefined || trackValueEquals(first.vIn, first.vOut)) continue;
    keys ??= new Map(doc.keys);
    keys.set(first.id, { ...first, vIn: first.vOut });
    count++;
  }
  if (keys === null) return doc;
  r.add('changed', 'pkg.report.firstKeyJump', undefined, undefined, undefined, count);
  return { ...doc, keys };
}

/**
 * Notes the document keeps in another order than an RPE chart lists them (see the module header), reported to `r`.
 */
export function reportNoteOrder(chart: IrChart, r: ReportSink): void {
  let first = -1;
  let count = 0;
  chart.judgeLineList.forEach((line, li) => {
    let prev: { readonly t: Beat; readonly x: number } | null = null;
    for (const n of line.notes) {
      const t = Beats.tryFromTriple(n.startTime)?.beat ?? null;
      // A note with an invalid time is left out by Lift (its own entry).
      if (t === null) continue;
      if (prev !== null && (Beats.cmp(t, prev.t) < 0 || (Beats.eq(t, prev.t) && n.positionX < prev.x))) {
        if (first < 0) first = li;
        count++;
        return;
      }
      prev = { t, x: n.positionX };
    }
  });
  if (count === 0) return;
  r.add('normalized', 'pkg.report.notesReordered', { lines: count }, `judgeLineList[${String(first)}].notes`, first);
}
